/**
 * Regras de qual empresa fica ativa e do que bloqueia a tela (plano de
 * convites, Fase 4). Funcoes puras, como em filialAtiva.ts: o AuthContext so
 * aplica.
 *
 * Quem participa de mais de uma empresa escolhe em qual esta operando; tudo o
 * mais (negocios, "Todos os negocios", plano, permissoes) vale dentro dela.
 */

/** Chave no localStorage. O valor leva versao para nunca confiar em formato antigo. */
export const EMPRESA_ATIVA_KEY = 'empresaAtiva';
const VERSAO = 1;

export type EstadoDaEmpresa = 'liberada' | 'suspensa' | 'inativa';

interface EmpresaResumo {
    id: string;
    souDono: boolean;
    estado: EstadoDaEmpresa;
}

/** O que gravar quando a pessoa escolhe uma empresa. */
export function valorSalvoDaEmpresa(id: string): string {
    return JSON.stringify({ v: VERSAO, id });
}

/** Id gravado, ou null se nao houver, estiver corrompido ou for de outra versao. */
export function empresaSalva(salvo: string | null): string | null {
    if (!salvo) return null;
    try {
        const parsed = JSON.parse(salvo) as { v?: unknown; id?: unknown };
        return parsed?.v === VERSAO && typeof parsed.id === 'string' ? parsed.id : null;
    } catch {
        return null;
    }
}

/**
 * Empresa ativa ao carregar. A gravada, se a pessoa ainda participa dela;
 * senao a propria; sem propria, a primeira (participacao mais antiga). Quem
 * vem de antes desta chave (so tem `filialAtiva`) cai na propria, que e a
 * mesma regra do backend quando nao recebe `x-empresa-id`.
 * `regravar` indica que o valor salvo deve ser trocado por `ativa`.
 */
export function resolverEmpresaInicial<E extends EmpresaResumo>(
    empresas: E[],
    salvo: string | null,
): { ativa: E | null; regravar: boolean } {
    if (empresas.length === 0) return { ativa: null, regravar: false };

    const id = empresaSalva(salvo);
    const escolhida = id ? empresas.find((e) => e.id === id) : undefined;
    if (escolhida) return { ativa: escolhida, regravar: false };

    return { ativa: empresas.find((e) => e.souDono) ?? empresas[0], regravar: true };
}

/** Negocios da empresa ativa. Sem empresa conhecida (consulta falhou), todos, como antes. */
export function filiaisDaEmpresa<F extends { empresaId: string }>(filiais: F[], empresaId: string | null | undefined): F[] {
    return empresaId ? filiais.filter((f) => f.empresaId === empresaId) : filiais;
}

/**
 * O que a tela mostra no lugar do app:
 * - `pagamento`: a cobranca e de quem esta logado (dono da empresa ativa
 *   suspensa, ou a conta inteira suspensa por nao ter outra empresa);
 * - `empresa-suspensa`: o dono de outra pessoa nao pagou; o convidado fala
 *   com ele, nunca ve tela de pagamento;
 * - `empresa-inativa`: cobranca do dono cancelada.
 */
export type BloqueioDaTela = 'pagamento' | 'empresa-suspensa' | 'empresa-inativa' | null;

export function bloqueioDaTela(args: {
    /** O backend recusou a conta inteira (ACCOUNT_SUSPENDED). */
    contaSuspensa: boolean;
    statusDoUsuario: string | undefined;
    empresaAtiva: EmpresaResumo | null;
}): BloqueioDaTela {
    const { contaSuspensa, statusDoUsuario, empresaAtiva } = args;
    if (contaSuspensa) return 'pagamento';
    if (empresaAtiva) {
        if (empresaAtiva.estado === 'suspensa') return empresaAtiva.souDono ? 'pagamento' : 'empresa-suspensa';
        if (empresaAtiva.estado === 'inativa') return 'empresa-inativa';
        // O status do usuario e a cobranca da empresa DELE: suspenso participando
        // de outra empresa liberada continua usando a outra.
        return null;
    }
    // Sem empresas carregadas (o backend recusa /empresas/minhas quando a conta
    // inteira esta suspensa): vale o status do usuario, como sempre foi.
    return statusDoUsuario === 'suspended' ? 'pagamento' : null;
}

/** Marca a empresa que o backend acabou de recusar (402/403 no meio da sessao). */
export function marcarEmpresaBloqueada<E extends EmpresaResumo>(empresas: E[], empresaId: string, estado: EstadoDaEmpresa): E[] {
    return empresas.map((e) => (e.id === empresaId && e.estado !== estado ? { ...e, estado } : e));
}

/**
 * Abas Usuarios e Perfis de acesso: so o dono da empresa ativa, com a feature
 * `usuarios` E pelo menos uma vaga no plano. Plano sem vaga deixava o dono numa
 * aba onde nao podia convidar ninguem; a equipe LTQ (admin) ve tudo.
 */
export function podeGerirAcessosNaEmpresa(
    empresa: { souDono: boolean; plano: { features: string[]; maxUsuarios: number } | null } | null | undefined,
    isAdmin = false,
): boolean {
    if (!empresa?.souDono) return false;
    if (isAdmin) return true;
    const plano = empresa.plano;
    return !!plano && plano.features.includes('usuarios') && plano.maxUsuarios > 0;
}
